import { epicLabel } from "../../epics/model.ts";
import type { Todo } from "../../../domain/entities.ts";
import type { TaskCollectionPresentationState } from "../task-collection-presentation-controller.ts";
import { activeTags, compareTasks } from "../selectors.ts";
import type { TableColumn, TableSort } from "./preferences.ts";
import { isCustomColumn } from "./preferences.ts";

export function tableValue(task: Todo, key: TableColumn, state: TaskCollectionPresentationState): string {
  if (isCustomColumn(key)) {
    const value = task.customFields?.[key.slice(6)], field = state.customFields?.find(field => field.id === key.slice(6));
    return value == null ? "" : field?.kind === "date" ? state.formatDate(String(value)) : String(value);
  }
  if (key === "epic") return epicLabel(task, state.tasks, state.epics || []);
  if (key === "priority") return task.priority === "none" ? "" : task.priority;
  if (key === "assignee") return (task.assignedTo || []).map(state.memberEmail).join(", ");
  if (key === "collection") return task.categoryId ? state.categoryName(task.categoryId) : "";
  if (key === "space") return state.spaceName(task.rootId);
  if (key === "start") return task.startDate ? state.formatDate(task.startDate) : "";
  if (key === "due") return task.dueDate ? state.formatDate(task.dueDate) : "";
  if (key === "tags") return activeTags(task).map(tag => `#${tag}`).join(" ");
  if (key === "column") { const category = state.categories.find(item => item.id === task.categoryId); const column = activeTags(task)[0]; return category?.boardColumns?.includes(column) ? column : ""; }
  if (key === "subtasks") return task.subtasks?.length ? `${task.subtasks.filter(item => item.completed).length}/${task.subtasks.length}` : "";
  if (key === "comments") return task.comments?.length ? String(task.comments.length) : "";
  return state.formatTimestamp(new Date(key === "created" ? task.createdAt : task.updatedAt).toISOString());
}

function sortableValue(task: Todo, key: TableColumn, state: TaskCollectionPresentationState): string | number | null {
  if (isCustomColumn(key)) return task.customFields?.[key.slice(6)] ?? null;
  if (key === "start") return task.startDate || null;
  if (key === "due") return task.dueDate || null;
  if (key === "created") return task.createdAt;
  if (key === "updated") return task.updatedAt;
  if (key === "comments") return task.comments?.length || 0;
  if (key === "subtasks") return task.subtasks?.length ? task.subtasks.filter(item => item.completed).length / task.subtasks.length : null;
  return tableValue(task, key, state) || null;
}

export function compareTableTasks(left: Todo, right: Todo, key: TableSort | "manual", direction: "asc" | "desc", state: TaskCollectionPresentationState): number {
  if (["manual", "title", "created", "due", "priority", "status"].includes(key)) return compareTasks(left, right, key as "manual" | "title" | "created" | "due" | "priority" | "status", direction);
  const pinned = Number(!!right.pinned) - Number(!!left.pinned);
  if (pinned) return pinned;
  const a = sortableValue(left, key as TableColumn, state), b = sortableValue(right, key as TableColumn, state);
  if (a === null || b === null) return a === null && b === null ? compareTasks(left, right, "manual", "asc") : a === null ? 1 : -1;
  const difference = typeof a === "number" && typeof b === "number" ? a - b : String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
  return (direction === "desc" ? -difference : difference) || compareTasks(left, right, "manual", "asc");
}
